import React, { CSSProperties, ReactNode, useCallback, useEffect, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { useAssignProjectsHealthChecksMutation, useGetHealthCheckListLazyQuery, useGetProjectLazyQuery, HealthCheck } from '../../../generated/graphql';
import { FormMultiSelect, FormCheckbox } from '../../form';
import styles from './AddProjectHealthChecksDialog.module.css';

type AssignmentStatus = 'all' | 'some' | 'none';

type AddProjectHealthChecksDialogProps = {
    onClose: () => void,
    ids: string[],
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const AddProjectHealthChecksDialog = (props: AddProjectHealthChecksDialogProps) => {
    const { ids, style, visible, onClose } = props;
    const [assignProjectsHealthChecks, assignProjectsHealthChecksResult] = useAssignProjectsHealthChecksMutation();
    const [selectedHealthChecks, setSelectedHealthChecks] = useState<HealthCheck[]>([]);
    const [overwrite, setOverwrite] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [allHealthChecks, setAllHealthChecks] = useState<HealthCheck[]>([]);
    const [assignedMap, setAssignedMap] = useState<Record<string, AssignmentStatus>>({});
    const [getHealthCheckList] = useGetHealthCheckListLazyQuery({ fetchPolicy: "no-cache" });
    const [getProject] = useGetProjectLazyQuery({ fetchPolicy: "no-cache" });

    const fetchAllHealthChecks = useCallback(async () => {
        let after: string | undefined;
        let accumulated: HealthCheck[] = [];
        let hasNextPage = true;

        while (hasNextPage) {
            const { data } = await getHealthCheckList({
                variables: { contains: "", after },
            });
            const nodes = (data?.HealthCheckList?.nodes || []) as HealthCheck[];
            accumulated = [...accumulated, ...nodes];
            hasNextPage = data?.HealthCheckList?.pageInfo?.hasNextPage ?? false;
            after = data?.HealthCheckList?.pageInfo?.endCursor ?? undefined;
        }

        setAllHealthChecks(accumulated);
    }, [getHealthCheckList]);

    const fetchAssignedHealthChecks = useCallback(async () => {
        const projectResults = await Promise.all(
            ids.map(id => getProject({ variables: { id } }))
        );

        const healthCheckCounts: Record<string, number> = {};
        projectResults.forEach(result => {
            const projectHealthCheckIds = result.data?.Project?.healthCheckIds || [];
            projectHealthCheckIds.forEach(hcId => {
                if (hcId) {
                    healthCheckCounts[hcId] = (healthCheckCounts[hcId] || 0) + 1;
                }
            });
        });

        const totalProjects = ids.length;
        const statusMap: Record<string, AssignmentStatus> = {};
        Object.entries(healthCheckCounts).forEach(([hcId, count]) => {
            statusMap[hcId] = count >= totalProjects ? 'all' : 'some';
        });

        setAssignedMap(statusMap);
    }, [ids, getProject]);

    useEffect(() => {
        fetchAllHealthChecks();
        fetchAssignedHealthChecks();
    }, [fetchAllHealthChecks, fetchAssignedHealthChecks]);

    const renderItem = (item: HealthCheck) => {
        const status = assignedMap[item.id || ''];
        const badge = status === 'all'
            ? <span className={styles.assignedBadge}>Assigned</span>
            : status === 'some'
                ? <span className={styles.partialBadge}>Partially Assigned</span>
                : null;

        return (
            <span className={styles.itemContent}>
                <span>{item.name}</span>
                {badge}
            </span>
        );
    };

    const handleSubmit = () => {
        if (selectedHealthChecks.length === 0) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        setValidationError('');
        const healthCheckIds = selectedHealthChecks.map(t => t.id || "");
        assignProjectsHealthChecks({
            variables: { ids, healthCheckIds, overwrite }
        })
            .then(() => { onClose(); })
            .catch(() => {});
    };

    return (
        <DialogBase
            title="Add Health Checks"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.AddProjectHealthChecksDialog}
            style={style}
            data-testid="AddProjectHealthChecksDialog"
            isLoading={assignProjectsHealthChecksResult.loading}>
            <FormMultiSelect
                label="Health Checks"
                name="healthChecks"
                value={selectedHealthChecks}
                onChange={(v) => setSelectedHealthChecks(v as HealthCheck[])}
                options={allHealthChecks}
                textField="name"
                renderItem={renderItem}
            />
            <FormCheckbox
                label="Overwrite"
                name="overwrite"
                checked={overwrite}
                onChange={setOverwrite}
            />
        </DialogBase>
    );
};

export default AddProjectHealthChecksDialog;